import { useEffect, useState } from "react";

import { AssignmentList } from "../../api/AssignmentService";

import { LoadingDiv, SweetAlertShow } from "../master/Master";

import Header from "../master/SideBar";

import Nav from "../master/NavBar";

import usePrivateLink from "../config/usePrivateLink";

import { formatToDate } from "../master/FormatToDateTime";

import { Link } from "react-router-dom";

import Pagination from "../components/Pagination";

import AcademicHeader from "../Academic/AcademicHeader";

import { getBatchList } from "../../api/BatchService";

import { subjectList } from "../../api/Subjects";

export default function Assignment() {
  const hasAccess = usePrivateLink();

  const [loading, setLoading] = useState(false);

  // =========================================================

  // MAIN ASSIGNMENT DATA

  // =========================================================

  const [assignments, setAssignments] = useState([]);

  const [data, setdata] = useState([]);

  // =========================================================

  // BATCH / SUBJECT MASTER DATA

  // =========================================================

  const [batches, setBatches] = useState([]);

  const [subjects, setSubjects] = useState([]);

  // =========================================================

  // ASSIGNMENT TAB FILTERS

  // =========================================================

  const [filters, setFilters] = useState({
    title: "",

    type: "",

    target_type: "",

    batch_id: "",

    subject_id: "",

    status: "",

    due_date: "",

    // Assignment created/assigned date

    date: "",
  });

  // =========================================================

  // ASSIGNMENT DATE QUICK FILTER

  // =========================================================

  const [assignmentDateType, setAssignmentDateType] = useState("");

  const [assignmentCustomDate, setAssignmentCustomDate] = useState("");

  // =========================================================

  // GET LOCAL DATE

  // =========================================================

  const getTodayDate = () => {
    const today = new Date();

    const year = today.getFullYear();

    const month = String(today.getMonth() + 1).padStart(2, "0");

    const day = String(today.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  // =========================================================

  // GET YESTERDAY DATE

  // =========================================================

  const getYesterdayDate = () => {
    const yesterday = new Date();

    yesterday.setDate(yesterday.getDate() - 1);

    const year = yesterday.getFullYear();

    const month = String(yesterday.getMonth() + 1).padStart(2, "0");

    const day = String(yesterday.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  // =========================================================

  // GET ASSIGNMENT SELECTED DATE

  // =========================================================

  const getAssignmentSelectedDate = () => {
    if (assignmentDateType === "today") {
      return getTodayDate();
    }

    if (assignmentDateType === "yesterday") {
      return getYesterdayDate();
    }

    if (assignmentDateType === "custom") {
      return assignmentCustomDate;
    }

    return "";
  };

  // =========================================================

  // GET ASSIGNMENT LIST

  //

  // Date is passed to API:

  // &date=2026-08-15

  // =========================================================

  const getAssignmentList = async () => {
    setLoading(true);

    let addUrl = "";

    Object.keys(filters).forEach((key) => {
      const value = filters[key];

      if (value) {
        addUrl += `&${key}=${encodeURIComponent(value)}`;
      }
    });

    try {
      const response = await AssignmentList(addUrl);

      if (response?.data?.status) {
        setAssignments(response?.data?.data?.data || []);

        setdata(response?.data?.data || []);
      } else {
        setAssignments([]);

        setdata([]);

        SweetAlertShow(
          "info",

          "Warning",

          response?.data?.message || "No assignment found.",
        );
      }
    } catch (error) {
      setAssignments([]);

      setdata([]);

      SweetAlertShow(
        "error",

        "Error",

        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================

  // GET BATCHES

  // =========================================================

  const getBatch = async () => {
    try {
      const response = await getBatchList();

      if (response?.data?.status) {
        setBatches(response?.data?.data?.data || response?.data?.data || []);
      }
    } catch (error) {
      console.log("BATCH ERROR:", error);
    }
  };

  // =========================================================

  // GET SUBJECTS

  // =========================================================

  const getSubjects = async () => {
    try {
      const response = await subjectList();

      if (response?.data?.status) {
        setSubjects(response?.data?.data?.data || response?.data?.data || []);
      }
    } catch (error) {
      console.log("SUBJECT ERROR:", error);
    }
  };

  // =========================================================

  // INITIAL LOAD

  // =========================================================

  useEffect(() => {
    getBatch();

    getSubjects();
  }, []);

  // =========================================================

  // INITIAL / FILTERED ASSIGNMENT API

  // =========================================================

  useEffect(() => {
    getAssignmentList();
  }, [filters]);

  // =========================================================

  // WHEN ASSIGNMENT DATE QUICK FILTER CHANGES

  // UPDATE API FILTER

  // =========================================================

  useEffect(() => {
    const selectedDate = getAssignmentSelectedDate();

    setFilters((prev) => {
      if (prev.date === selectedDate) {
        return prev;
      }

      return {
        ...prev,

        date: selectedDate,
      };
    });
  }, [assignmentDateType, assignmentCustomDate]);

  // =========================================================

  // GET TARGET

  // =========================================================

  const getTarget = (item) => {
    const target = item?.target_info;

    if (!target) {
      return "-";
    }

    if (target.type === "all") {
      return (
        <div>
          <span className="badge bg-label-primary">All Students</span>
        </div>
      );
    }

    if (target.type === "batch") {
      return (
        <div>
          <span className="badge bg-label-info mb-1">Batch</span>

          <div className="small fw-semibold">
            {target?.batch_names?.length > 0
              ? target.batch_names.join(", ")
              : "-"}
          </div>
        </div>
      );
    }

    if (target.type === "student") {
      return (
        <div>
          <span className="badge bg-label-warning">Selected Students</span>

          {target?.names?.length > 0 && (
            <div className="small mt-1">
              {target.names.join(", ")}

              {target?.student_ids?.length > 5 && " ..."}
            </div>
          )}
        </div>
      );
    }

    if (target.type === "staff") {
      return (
        <div>
          <span className="badge bg-label-success">Selected Staff</span>

          {target?.names?.length > 0 && (
            <div className="small mt-1">
              {target.names.join(", ")}

              {target?.staff_ids?.length > 5 && " ..."}
            </div>
          )}
        </div>
      );
    }

    return "-";
  };

  // =========================================================

  // GET SUBJECT NAME

  // =========================================================

  const getSubjectName = (subjectId) => {
    const subject = subjects.find(
      (item) => String(item?.id) === String(subjectId),
    );

    return subject?.name || "-";
  };

  // =========================================================

  // RESET ASSIGNMENT FILTERS

  // =========================================================

  const clearFilters = () => {
    setAssignmentDateType("");

    setAssignmentCustomDate("");

    setFilters({
      title: "",

      type: "",

      target_type: "",

      batch_id: "",

      subject_id: "",

      status: "",

      due_date: "",

      date: getTodayDate(),
    });
  };

  // =========================================================

  // ASSIGNMENT DATE BUTTONS

  // =========================================================

  const selectAssignmentToday = () => {
    setAssignmentDateType("today");

    setAssignmentCustomDate("");
  };

  const selectAssignmentYesterday = () => {
    setAssignmentDateType("yesterday");

    setAssignmentCustomDate("");
  };

  const selectAssignmentCustom = () => {
    setAssignmentDateType("custom");

    if (!assignmentCustomDate) {
      setAssignmentCustomDate(getTodayDate());
    }
  };

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid">
                <div className="row mt-4">
                  <AcademicHeader />

                  <div className="col-12">
                    {/* =====================================================

                        HEADER

                    ====================================================== */}

                    <div className="card mb-4">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h4 className="text-primary mb-1">
                              Assignment Management
                            </h4>

                            <small className="text-muted">
                              Manage and track assignments.
                            </small>
                          </div>
                          <div className="d-flex gap-2">
                            {hasAccess("/assignment/add") && (
                              <Link
                                to="/assignment/add"
                                className="btn btn-primary"
                              >
                                <i className="ri-add-line me-1"></i>
                                Add Assignment
                              </Link>
                            )}
                            {hasAccess("/assignments/student-status") && (
                              <Link
                                to="/assignments/student-status"
                                className="btn btn-outline-primary me-2"
                              >
                                <i className="ri-user-search-line me-1"></i>
                                Student Assignments
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* =====================================================

                        ASSIGNMENTS TAB

                    ====================================================== */}

                    <>
                      {/* =============================================

                            ASSIGNMENT DATE FILTER

                        ============================================== */}

                      <div className="card mb-4">
                        <div className="card-body">
                          <div className="row align-items-end g-3">
                            <div className="col-md-7">
                              <label className="form-label d-block mb-2">
                                Assignment Date
                              </label>

                              <div className="btn-group" role="group">
                                <button
                                  type="button"
                                  className={`btn ${
                                    assignmentDateType === "today"
                                      ? "btn-primary"
                                      : "btn-outline-primary"
                                  }`}
                                  onClick={selectAssignmentToday}
                                >
                                  <i className="ri-calendar-check-line me-1"></i>
                                  Today
                                </button>

                                <button
                                  type="button"
                                  className={`btn ${
                                    assignmentDateType === "yesterday"
                                      ? "btn-primary"
                                      : "btn-outline-primary"
                                  }`}
                                  onClick={selectAssignmentYesterday}
                                >
                                  <i className="ri-calendar-line me-1"></i>
                                  Yesterday
                                </button>

                                <button
                                  type="button"
                                  className={`btn ${
                                    assignmentDateType === "custom"
                                      ? "btn-primary"
                                      : "btn-outline-primary"
                                  }`}
                                  onClick={selectAssignmentCustom}
                                >
                                  <i className="ri-calendar-2-line me-1"></i>
                                  Custom Date
                                </button>
                              </div>
                            </div>

                            {assignmentDateType === "custom" && (
                              <div className="col-md-3 form-floating form-floating-outline">
                                <input
                                  type="date"
                                  className="form-control"
                                  value={assignmentCustomDate}
                                  max={getTodayDate()}
                                  onChange={(e) =>
                                    setAssignmentCustomDate(e.target.value)
                                  }
                                />

                                <label>Select Date</label>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* =============================================

                            OTHER FILTERS

                        ============================================== */}

                      <div className="card mb-4">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">Assignment Filters</h5>

                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={clearFilters}
                            >
                              <i className="ri-refresh-line me-1"></i>
                              Reset
                            </button>
                          </div>

                          <div className="row g-3">
                            {/* TITLE */}

                            <div className="col-md-2 form-floating form-floating-outline">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Search Title"
                                value={filters.title}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,

                                    title: e.target.value,
                                  })
                                }
                              />

                              <label>Search Title</label>
                            </div>

                            {/* TYPE */}

                            <div className="col-md-2 form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={filters.type}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,

                                    type: e.target.value,
                                  })
                                }
                              >
                                <option value="">All Type</option>

                                <option value="homework">Homework</option>

                                <option value="task">Task</option>

                                <option value="project">Project</option>

                                <option value="activity">Activity</option>
                              </select>

                              <label>Assignment Type</label>
                            </div>

                            {/* TARGET */}

                            <div className="col-md-2 form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={filters.target_type}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,

                                    target_type: e.target.value,

                                    batch_id: "",
                                  })
                                }
                              >
                                <option value="">All Target</option>

                                <option value="all">All Students</option>

                                <option value="batch">Batch</option>

                                <option value="student">Student</option>

                                <option value="staff">Staff</option>
                              </select>

                              <label>Target</label>
                            </div>

                            {/* BATCH */}

                            {filters.target_type === "batch" && (
                              <div className="col-md-2 form-floating form-floating-outline">
                                <select
                                  className="form-select"
                                  value={filters.batch_id}
                                  onChange={(e) =>
                                    setFilters({
                                      ...filters,

                                      batch_id: e.target.value,
                                    })
                                  }
                                >
                                  <option value="">All Batch</option>

                                  {batches.map((batch) => (
                                    <option key={batch.id} value={batch.id}>
                                      {batch?.title}

                                      {batch?.course?.title
                                        ? ` (${batch.course.title})`
                                        : ""}
                                    </option>
                                  ))}
                                </select>

                                <label>Batch</label>
                              </div>
                            )}

                            {/* SUBJECT */}

                            <div className="col-md-2 form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={filters.subject_id}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,

                                    subject_id: e.target.value,
                                  })
                                }
                              >
                                <option value="">All Subjects</option>

                                {subjects.map((subject) => (
                                  <option key={subject.id} value={subject.id}>
                                    {subject.name}
                                  </option>
                                ))}
                              </select>

                              <label>Subject</label>
                            </div>

                            {/* DUE DATE */}

                            <div className="col-md-2 form-floating form-floating-outline">
                              <input
                                type="date"
                                className="form-control"
                                value={filters.due_date}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,

                                    due_date: e.target.value,
                                  })
                                }
                              />

                              <label>Due Date</label>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* =============================================

                            ASSIGNMENT TABLE

                        ============================================== */}

                      <div className="card">
                        <div className="card-body">
                          <div className="table-responsive">
                            <table className="table table-hover align-middle">
                              <thead>
                                <tr>
                                  <th>#</th>

                                  <th>Title</th>

                                  <th>Type</th>

                                  <th>Subject</th>

                                  <th>Target</th>

                                  <th>Due Date</th>

                                  <th>Status</th>

                                  <th>Action</th>
                                </tr>
                              </thead>

                              <tbody>
                                {assignments.length > 0 ? (
                                  assignments.map((item, i) => (
                                    <tr key={item?.id}>
                                      <td>{(data?.from || 1) + i}</td>

                                      <td>
                                        <div className="fw-semibold text-primary">
                                          {item?.title}
                                        </div>

                                        {item?.description && (
                                          <small className="text-muted">
                                            {item.description.length > 50
                                              ? item.description.substring(
                                                  0,

                                                  50,
                                                ) + "..."
                                              : item.description}
                                          </small>
                                        )}
                                      </td>

                                      <td>
                                        <span className="badge bg-label-secondary">
                                          {item?.type}
                                        </span>
                                      </td>

                                      <td>
                                        {item?.target_info?.subject?.name ? (
                                          <span className="badge bg-label-info">
                                            {item?.target_info?.subject?.name}
                                          </span>
                                        ) : (
                                          "-"
                                        )}
                                      </td>

                                      <td>{getTarget(item)}</td>

                                      <td>{formatToDate(item?.due_date)}</td>

                                      <td>
                                        <span
                                          className={`badge ${
                                            item.status === "1"
                                              ? "bg-label-success"
                                              : item.status === "0"
                                                ? "bg-label-primary"
                                                : "bg-label-danger"
                                          }`}
                                        >
                                          {item.status === "1"
                                            ? "Active"
                                            : item.status === "0"
                                              ? "Inactive"
                                              : "Completed"}
                                        </span>
                                      </td>

                                      <td>
                                        {hasAccess("/assignments/view") && (
                                          <Link
                                            to={`/assignments/view?id=${item.id}&type=${item?.target_info?.type}`}
                                            className="btn btn-sm btn-outline-primary"
                                            title="View"
                                          >
                                            <i className="ri-eye-line"></i>
                                          </Link>
                                        )}
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td
                                      colSpan="8"
                                      className="text-center py-4"
                                    >
                                      No Data Found
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>

                            <Pagination
                              data={data}
                              setFilters={setFilters}
                              filters={filters}
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
